import React from 'react';
import { clsx } from 'clsx';
import { UserIcon } from '@heroicons/react/24/solid';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

export interface AvatarProps extends React.HTMLAttributes<HTMLDivElement> {
  src?: string;
  alt?: string;
  name?: string;
  size?: AvatarSize;
  status?: 'online' | 'offline' | 'busy' | 'away';
  showStatus?: boolean;
}

const sizeStyles: Record<AvatarSize, { container: string; text: string; icon: string; status: string }> = {
  xs: { container: 'w-6 h-6', text: 'text-[10px]', icon: 'w-3 h-3', status: 'w-1.5 h-1.5 border' },
  sm: { container: 'w-8 h-8', text: 'text-xs', icon: 'w-4 h-4', status: 'w-2 h-2 border' },
  md: { container: 'w-10 h-10', text: 'text-sm', icon: 'w-5 h-5', status: 'w-2.5 h-2.5 border-2' },
  lg: { container: 'w-12 h-12', text: 'text-base', icon: 'w-6 h-6', status: 'w-3 h-3 border-2' },
  xl: { container: 'w-14 h-14', text: 'text-lg', icon: 'w-7 h-7', status: 'w-3.5 h-3.5 border-2' },
  '2xl': { container: 'w-16 h-16', text: 'text-xl', icon: 'w-8 h-8', status: 'w-4 h-4 border-2' },
};

const statusColors: Record<string, string> = {
  online: 'bg-success-500',
  offline: 'bg-neutral-400',
  busy: 'bg-error-500',
  away: 'bg-warning-500',
};

// Generate initials from name
const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// Generate consistent color from name
const getColorFromName = (name: string): string => {
  const colors = [
    'bg-primary-500',
    'bg-secondary-500',
    'bg-success-500',
    'bg-warning-500',
    'bg-error-500',
    'bg-primary-600',
    'bg-secondary-600',
    'bg-success-600',
    'bg-warning-600',
    'bg-error-600',
  ];
  
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

export const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  (
    {
      src,
      alt,
      name,
      size = 'md',
      status,
      showStatus = false,
      className,
      ...props
    },
    ref
  ) => {
    const [imgError, setImgError] = React.useState(false);
    const styles = sizeStyles[size];
    const showImage = src && !imgError;
    const showInitials = !showImage && name;
    const showIcon = !showImage && !name;

    return (
      <div ref={ref} className={clsx('relative inline-flex', className)} {...props}>
        <div
          className={clsx(
            'rounded-full flex items-center justify-center overflow-hidden',
            styles.container,
            showInitials && getColorFromName(name || ''),
            showIcon && 'bg-neutral-200'
          )}
        >
          {showImage && (
            <img
              src={src}
              alt={alt || name || 'Avatar'}
              className="w-full h-full object-cover"
              onError={() => setImgError(true)}
            />
          )}

          {showInitials && (
            <span className={clsx('font-semibold text-white', styles.text)}>
              {getInitials(name)}
            </span>
          )}

          {showIcon && (
            <UserIcon className={clsx(styles.icon, 'text-neutral-500')} />
          )}
        </div>

        {/* Status indicator */}
        {showStatus && status && (
          <span
            className={clsx(
              'absolute bottom-0 right-0 rounded-full border-white',
              styles.status,
              statusColors[status]
            )}
          />
        )}
      </div>
    );
  }
);

Avatar.displayName = 'Avatar';

// Avatar Group
export interface AvatarGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  max?: number;
  size?: AvatarSize;
}

export const AvatarGroup: React.FC<AvatarGroupProps> = ({
  children,
  max = 4,
  size = 'md',
  className,
  ...props
}) => {
  const childArray = React.Children.toArray(children);
  const visibleCount = Math.min(childArray.length, max);
  const extraCount = childArray.length - max;
  const styles = sizeStyles[size];

  return (
    <div className={clsx('flex -space-x-2', className)} {...props}>
      {childArray.slice(0, visibleCount).map((child, index) => (
        <div key={index} className="ring-2 ring-white rounded-full">
          {React.isValidElement(child) ? React.cloneElement(child as React.ReactElement<AvatarProps>, { size }) : child}
        </div>
      ))}
      {extraCount > 0 && (
        <div
          className={clsx(
            'rounded-full flex items-center justify-center bg-neutral-200 text-neutral-600 font-medium ring-2 ring-white',
            styles.container,
            styles.text
          )}
        >
          +{extraCount}
        </div>
      )}
    </div>
  );
};

AvatarGroup.displayName = 'AvatarGroup';

export default Avatar;

